import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Editorial article-header for the top of a page.
 *
 * Replaces the inline header inside <PageShell>. Reads like a magazine
 * spread:
 *   - small uppercase eyebrow / kicker
 *   - large display title with tight tracking
 *   - muted lede subtitle
 *   - optional colored icon badge
 *   - optional metadata row (timestamps, tags, status pills)
 *   - optional actions on the inline-end (right in RTL)
 *
 * Spacing is generous on purpose — heroes want breathing room.
 */
const ICON_TINTS = {
  blue: { bg: "bg-blue-500/12", fg: "text-blue-600" },
  violet: { bg: "bg-violet-500/12", fg: "text-violet-600" },
  amber: { bg: "bg-amber-500/12", fg: "text-amber-600" },
  emerald: { bg: "bg-emerald-500/12", fg: "text-emerald-600" },
  rose: { bg: "bg-rose-500/12", fg: "text-rose-600" },
  slate: { bg: "bg-slate-500/12", fg: "text-slate-600" },
  teal: { bg: "bg-teal-500/12", fg: "text-teal-600" },
  pink: { bg: "bg-pink-500/12", fg: "text-pink-600" },
  stone: { bg: "bg-stone-500/10", fg: "text-stone-700" },
} as const;

export type HeroIconTint = keyof typeof ICON_TINTS;

export type HeroProps = {
  /** Small uppercase kicker above the title. */
  eyebrow?: string;
  /** Main display heading. */
  title: string;
  /** Muted lede subtitle. */
  subtitle?: string;
  /** Optional Lucide icon rendered in a colored badge. */
  icon?: LucideIcon;
  /** Tint used for the icon badge. Default: amber. */
  iconColor?: HeroIconTint;
  /** Optional metadata row (timestamps, tags, status). Below the title. */
  meta?: ReactNode;
  /** Optional actions on the inline-end. */
  actions?: ReactNode;
  /** Add a soft amber gradient backdrop behind the hero. Default: false. */
  withBackdrop?: boolean;
  /** Extra classes merged onto the wrapper. */
  className?: string;
};

export function Hero({
  eyebrow,
  title,
  subtitle,
  icon: Icon,
  iconColor = "amber",
  meta,
  actions,
  withBackdrop = false,
  className,
}: HeroProps) {
  const tint = ICON_TINTS[iconColor];

  return (
    <header
      className={cn(
        "relative",
        withBackdrop && [
          "rounded-3xl px-6 py-8 sm:px-8 sm:py-10",
          "bg-gradient-to-bl from-amber-50/70 via-white to-white",
          "border border-stone-200/60",
        ],
        !withBackdrop && "pb-2",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            {Icon ? (
              <div
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-2xl shrink-0",
                  tint.bg,
                )}
              >
                <Icon className={cn("h-5 w-5", tint.fg)} />
              </div>
            ) : null}

            <div className="min-w-0">
              {eyebrow ? (
                <p
                  className={cn(
                    "text-[10px] font-semibold uppercase",
                    "tracking-[0.22em] text-amber-700/80",
                    "mb-2",
                  )}
                >
                  {eyebrow}
                </p>
              ) : null}

              <h1
                className={cn(
                  "text-3xl sm:text-[2rem] font-bold tracking-tight",
                  "text-stone-900 leading-[1.15]",
                )}
              >
                {title}
              </h1>
            </div>
          </div>

          {subtitle ? (
            <p
              className={cn(
                "mt-3 max-w-2xl text-base sm:text-[17px]",
                "leading-[1.65] text-stone-600",
              )}
            >
              {subtitle}
            </p>
          ) : null}

          {meta ? (
            <div
              className={cn(
                "mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5",
                "text-xs text-stone-500",
              )}
            >
              {meta}
            </div>
          ) : null}
        </div>

        {actions ? (
          <div className="shrink-0 flex items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}
